<template>
    <div class="company-timeline">
        <div class="company-timeline__header">
            <button v-if="showPast && pastItems.length > 1" class="company-timeline__collapse-btn" @click="showPast = false">
                {{ t.hidePastCount.replace(':count', pastItems.length) }}
            </button>
            <div class="company-timeline__legend">
                <span class="company-timeline__legend-item">
                    <span class="company-timeline__legend-dot company-timeline__legend-dot--deadline"></span>
                    {{ t.legendDeadline }}
                </span>
                <span class="company-timeline__legend-item">
                    <span class="company-timeline__legend-dot company-timeline__legend-dot--forum"></span>
                    {{ t.legendForum }}
                </span>
                <span class="company-timeline__legend-item">
                    <span class="company-timeline__legend-dot company-timeline__legend-dot--reservation"></span>
                    {{ t.legendReservation }}
                </span>
                <span class="company-timeline__legend-item">
                    <span class="company-timeline__legend-dot company-timeline__legend-dot--billing"></span>
                    {{ t.legendBilling }}
                </span>
            </div>
        </div>
        <div class="company-timeline__inner" :style="{ minWidth: (visibleItems.length * 90) + 'px' }">
        <div class="company-timeline__row company-timeline__row--above">
            <div
                v-for="(item, index) in visibleItems"
                :key="'above-' + index"
                class="company-timeline__cell"
            >
                <div v-if="index % 2 === 0" class="company-timeline__label" :class="labelClasses(item)" @click="onDotClick(item)">
                    <span class="company-timeline__date">{{ formatDate(item.date) }}</span>
                    <span class="company-timeline__title">{{ item.label }}</span>
                    <span v-if="(item.type === 'today' || item.type === 'forum') && item.isGroup" class="company-timeline__badge" :class="item.type === 'today' ? 'company-timeline__badge--today' : 'company-timeline__badge--forum-group'">
                        {{ (item.type === 'forum' ? (item.count === 1 ? t.forumGroupCountSingular : t.forumGroupCount) : (item.count === 1 ? t.groupCountSingular : t.groupCount)).replace(':count', item.count) }}
                    </span>
                    <span v-if="item.isPaid" class="company-timeline__badge company-timeline__badge--paid">
                        {{ t.paid }}
                    </span>
                    <span v-else-if="item.isOverdue" class="company-timeline__badge company-timeline__badge--overdue">
                        {{ t.overdue }}
                    </span>
                    <span v-else-if="item.isExpired" class="company-timeline__badge company-timeline__badge--expired">
                        {{ t.expired }}
                    </span>
                    <span v-else-if="item.isApproaching" class="company-timeline__badge company-timeline__badge--warning">
                        {{ t.soon }}
                    </span>
                </div>
            </div>
        </div>

        <div class="company-timeline__row company-timeline__row--dots">
            <div class="company-timeline__line" :style="{ left: lineOffset, right: lineOffset }"></div>
            <div class="company-timeline__progress" :style="{ left: lineOffset, width: todayProgressWidth }"></div>
            <div
                v-for="(item, index) in visibleItems"
                :key="'dot-' + index"
                class="company-timeline__cell"
            >
                <div
                    class="company-timeline__dot"
                    :class="dotClasses(item)"
                    @mouseenter="onGroupHover(item, index, $event)"
                    @mouseleave="onGroupLeave"
                    @click="onDotClick(item)"
                >
                    <span v-if="item.isGroup" class="company-timeline__dot-count">{{ item.count }}</span>
                </div>
                <div v-if="item.isGroup && hoveredGroup === index"
                     class="company-timeline__popover"
                     :style="popoverStyle"
                     @mouseenter="onPopoverHover"
                     @mouseleave="onGroupLeave"
                >
                    <div class="company-timeline__popover-title">{{ item.label }}</div>
                    <ul class="company-timeline__popover-list">
                        <li
                            v-for="(child, i) in item.children"
                            :key="i"
                            class="company-timeline__popover-item"
                            :class="{ 'company-timeline__popover-item--past': child.isPast }"
                        >
                            <span class="company-timeline__popover-dot" :class="popoverDotClasses(child)"></span>
                            <span class="company-timeline__popover-date">{{ formatDate(child.date) }}</span>
                            <span class="company-timeline__popover-label">{{ child.label }}</span>
                            <span v-if="child.isExpired" class="company-timeline__badge company-timeline__badge--expired">{{ t.expired }}</span>
                        </li>
                    </ul>
                </div>
            </div>
        </div>

        <div class="company-timeline__row company-timeline__row--below">
            <div
                v-for="(item, index) in visibleItems"
                :key="'below-' + index"
                class="company-timeline__cell"
            >
                <div v-if="index % 2 !== 0" class="company-timeline__label" :class="labelClasses(item)" @click="onDotClick(item)">
                    <span class="company-timeline__date">{{ formatDate(item.date) }}</span>
                    <span class="company-timeline__title">{{ item.label }}</span>
                    <span v-if="(item.type === 'today' || item.type === 'forum') && item.isGroup" class="company-timeline__badge" :class="item.type === 'today' ? 'company-timeline__badge--today' : 'company-timeline__badge--forum-group'">
                        {{ (item.type === 'forum' ? (item.count === 1 ? t.forumGroupCountSingular : t.forumGroupCount) : (item.count === 1 ? t.groupCountSingular : t.groupCount)).replace(':count', item.count) }}
                    </span>
                    <span v-if="item.isPaid" class="company-timeline__badge company-timeline__badge--paid">
                        {{ t.paid }}
                    </span>
                    <span v-else-if="item.isOverdue" class="company-timeline__badge company-timeline__badge--overdue">
                        {{ t.overdue }}
                    </span>
                    <span v-else-if="item.isExpired" class="company-timeline__badge company-timeline__badge--expired">
                        {{ t.expired }}
                    </span>
                    <span v-else-if="item.isApproaching" class="company-timeline__badge company-timeline__badge--warning">
                        {{ t.soon }}
                    </span>
                </div>
            </div>
        </div>
        </div>

        <div v-if="activeGroup" class="company-timeline__overlay" @click.self="activeGroup = null">
            <div class="company-timeline__modal">
                <div class="company-timeline__modal-header">
                    <strong>{{ activeGroup.label }} &mdash; {{ formatDate(activeGroup.date) }}</strong>
                    <button class="company-timeline__modal-close" @click="activeGroup = null">&times;</button>
                </div>
                <ul class="company-timeline__popover-list">
                    <li
                        v-for="(child, i) in activeGroup.children"
                        :key="i"
                        class="company-timeline__popover-item"
                        :class="{ 'company-timeline__popover-item--past': child.isPast }"
                    >
                        <span class="company-timeline__popover-dot" :class="popoverDotClasses(child)"></span>
                        <span class="company-timeline__popover-date">{{ formatDate(child.date) }}</span>
                        <span class="company-timeline__popover-label">{{ child.label }}</span>
                        <span v-if="child.isExpired" class="company-timeline__badge company-timeline__badge--expired">{{ t.expired }}</span>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment';

export default {
    props: {
        events: {
            type: Array,
            required: true
        },
        translations: {
            type: Object,
            default: () => ({})
        },
        fakeToday: {
            type: String,
            default: ''
        },
    },
    data() {
        return {
            showPast: false,
            activeGroup: null,
            hoveredGroup: null,
            hoverTimeout: null,
            popoverStyle: {}
        };
    },
    computed: {
        t() {
            return {
                today: "Aujourd'hui",
                soon: 'Bientôt',
                overdue: 'En retard',
                expired: 'Expiré',
                paid: 'Payée',
                hidePastCount: ':count échéances passées',
                pastCount: ':count échéances passées',
                groupCount: ':count échéances',
                groupCountSingular: ':count échéance',
                forumGroupCount: ':count prestations',
                forumGroupCountSingular: ':count prestation',
                legendDeadline: 'Échéance',
                legendForum: 'Forum',
                legendReservation: 'Réservation',
                legendBilling: 'Facturation',
                ...this.translations
            };
        },
        today() {
            return this.fakeToday ? moment(this.fakeToday).startOf('day') : moment().startOf('day');
        },
        sortedEvents() {
            return this.events
                .map(event => {
                    const date = moment(event.date);
                    const isPast = date.isBefore(this.today);
                    return {
                        ...event,
                        date,
                        isPast,
                        isToday: date.isSame(this.today, 'day'),
                        isApproaching: date.isAfter(this.today) &&
                            date.diff(this.today, 'days') <= 3,
                        isOverdue: isPast && event.isOverdue === true,
                        isExpired: isPast && !event.isOverdue && !event.isPaid && event.type === 'deadline',
                        isPaid: event.isPaid === true
                    };
                })
                .sort((a, b) => a.date.valueOf() - b.date.valueOf());
        },
        todayEvent() {
            return {
                label: this.t.today,
                date: this.today,
                isPast: false,
                isToday: true,
                isApproaching: false,
                type: 'today'
            };
        },
        allItems() {
            const items = [];
            let todayInserted = false;
            const todayEvents = this.sortedEvents.filter(e => e.date.isSame(this.today, 'day'));
            const otherEvents = this.sortedEvents.filter(e => !e.date.isSame(this.today, 'day'));
            let i = 0;

            const todayItem = todayEvents.length > 0
                ? { ...this.todayEvent, isGroup: true, count: todayEvents.length, children: todayEvents }
                : this.todayEvent;

            while (i < otherEvents.length) {
                const event = otherEvents[i];

                if (!todayInserted && event.date.isAfter(this.today)) {
                    items.push(todayItem);
                    todayInserted = true;
                }

                const sameDay = [event];
                while (i + 1 < otherEvents.length && otherEvents[i + 1].date.isSame(event.date, 'day')) {
                    i++;
                    sameDay.push(otherEvents[i]);
                }

                if (sameDay.length > 1) {
                    items.push(this.createGroup(sameDay));
                } else {
                    items.push(event);
                }

                i++;
            }

            if (!todayInserted) {
                items.push(todayItem);
            }

            return items;
        },
        pastItems() {
            return this.allItems.filter(item => {
                if (!item.isPast || item.isToday || item.type === 'today') {
                    return false;
                }
                // Overdue unpaid billing events must always remain visible, not collapsed
                if (item.isOverdue) {
                    return false;
                }
                if (item.isGroup && item.children) {
                    const hasOverdue = item.children.some(child => child.isOverdue);
                    if (hasOverdue) {
                        return false;
                    }
                }
                return true;
            });
        },
        visibleItems() {
            if (this.showPast || this.pastItems.length <= 1) {
                return this.allItems;
            }

            const futureItems = this.allItems.filter(item => {
                if (!item.isPast || item.isToday || item.type === 'today') {
                    return true;
                }
                // Keep overdue billing items visible even when past is collapsed
                if (item.isOverdue) {
                    return true;
                }
                if (item.isGroup && item.children) {
                    return item.children.some(child => child.isOverdue);
                }
                return false;
            });

            const pastGroup = {
                label: this.t.pastCount.replace(':count', this.pastItems.length),
                date: this.pastItems[0].date,
                isPast: true,
                isToday: false,
                isApproaching: false,
                isGroup: false,
                isPastCollapse: true,
                type: 'past-collapse'
            };

            return [pastGroup, ...futureItems];
        },
        todayIndex() {
            return this.visibleItems.findIndex(item => item.type === 'today' || item.isToday);
        },
        lineOffset() {
            const count = this.visibleItems.length;
            if (count === 0) return '0%';
            return `${50 / count}%`;
        },
        todayProgressWidth() {
            const count = this.visibleItems.length;
            if (count === 0 || this.todayIndex === -1) return '0%';
            const usablePercent = 100 - (100 / count);
            return `${(this.todayIndex / (count - 1)) * usablePercent}%`;
        }
    },
    methods: {
        formatDate(date) {
            return moment(date).format('DD MMM');
        },
        dotClasses(item) {
            return {
                'company-timeline__dot--past': item.isPast && !item.isToday,
                'company-timeline__dot--today': item.isToday,
                'company-timeline__dot--approaching': item.isApproaching,
                'company-timeline__dot--group': item.isGroup && item.type !== 'today' && item.type !== 'forum',
                'company-timeline__dot--forum-group': item.isGroup && item.type === 'forum',
                'company-timeline__dot--reservation': item.type === 'reservation',
                'company-timeline__dot--forum': item.type === 'forum',
                'company-timeline__dot--billing': item.type === 'billing' && !item.isOverdue,
                'company-timeline__dot--overdue': item.isOverdue,
                'company-timeline__dot--today-marker': item.type === 'today',
                'company-timeline__dot--has-events': item.type === 'today' && item.isGroup,
                'company-timeline__dot--past-collapse': item.isPastCollapse
            };
        },
        labelClasses(item) {
            return {
                'company-timeline__label--past': item.isPast && !item.isToday && !item.isPastCollapse,
                'company-timeline__label--today-marker': item.type === 'today',
                'company-timeline__label--past-collapse': item.isPastCollapse,
                'company-timeline__label--forum-group': item.type === 'forum' && item.isGroup
            };
        },
        createGroup(events) {
            const forumEvent = events.find(e => e.type === 'forum');
            const extras = forumEvent ? events.filter(e => e !== forumEvent) : [];

            if (forumEvent && extras.length > 0) {
                return {
                    ...forumEvent,
                    isGroup: true,
                    count: extras.length,
                    children: extras
                };
            }

            return {
                label: (events.length === 1 ? this.t.groupCountSingular : this.t.groupCount).replace(':count', events.length),
                date: events[0].date,
                isPast: events.every(e => e.isPast),
                isToday: false,
                isApproaching: false,
                isGroup: true,
                count: events.length,
                children: events
            };
        },
        onGroupHover(item, index, event) {
            if (item.isGroup && !this.isMobile()) {
                clearTimeout(this.hoverTimeout);
                const dot = event.currentTarget;
                const rect = dot.getBoundingClientRect();
                this.popoverStyle = {
                    top: (rect.bottom + 4) + 'px',
                    left: rect.left + (rect.width / 2) + 'px',
                    transform: 'translateX(-50%)'
                };
                this.hoveredGroup = index;
            }
        },
        onPopoverHover() {
            clearTimeout(this.hoverTimeout);
        },
        onGroupLeave() {
            this.hoverTimeout = setTimeout(() => {
                this.hoveredGroup = null;
            }, 150);
        },
        onDotClick(item) {
            if (item.isPastCollapse) {
                this.showPast = true;
            } else if (item.isGroup && this.isMobile()) {
                this.activeGroup = item;
            }
        },
        popoverDotClasses(child) {
            return {
                'company-timeline__popover-dot--forum': child.type === 'forum',
                'company-timeline__popover-dot--reservation': child.type === 'reservation',
                'company-timeline__popover-dot--prestation': child.type === 'prestation',
                'company-timeline__popover-dot--billing': child.type === 'billing',
                'company-timeline__popover-dot--overdue': child.isOverdue,
                'company-timeline__popover-dot--expired': child.isExpired,
                'company-timeline__popover-dot--paid': child.isPaid
            };
        },
        isMobile() {
            return window.innerWidth < 768;
        }
    }
};
</script>
